import { useCallback, useState } from 'react'
import { repository } from '../data'
import type { ScanResult } from '../data'
import { PageHeader } from './primitives'
import { useScanner } from './useScanner'
import { serialFromPayload } from './serialFromPayload'

/**
 * The default landing screen on a phone (§10.1).
 *
 * Point at a label, land on that thing's page. The typed fallback below the
 * camera is not a lesser path — it is what a scuffed label, a device with no
 * camera at all and a bench USB scanner all need, and it is why serials are
 * five unambiguous characters with a check digit.
 */
export function ScanView({ onResult }: { onResult: (r: ScanResult) => void }): JSX.Element {
  const [status, setStatus] = useState<string | null>(null)
  const [typed, setTyped] = useState('')

  const resolve = useCallback(
    async (raw: string) => {
      const serial = serialFromPayload(raw)
      if (!serial) {
        // Answered locally, with no round trip: the check character already
        // says this is not one of ours.
        setStatus('That is not a Kronos label.')
        return
      }
      setStatus(null)
      const result = await repository.scan(serial)
      if (result.kind === 'unknown') {
        setStatus(`${serial} is a valid serial but nothing is registered against it.`)
        return
      }
      onResult(result)
    },
    [onResult],
  )

  const scanner = useScanner((raw) => void resolve(raw))

  return (
    <div className="page">
      <PageHeader title="Scan" subtitle="Point the camera at a Kronos label, or type the serial printed under it." />

      <div className="scan-layout">
        <div className="stack" style={{ gap: 10 }}>
          <div className="scanner">
            <video ref={scanner.videoRef} playsInline muted />
            {scanner.running ? <div className="reticle" /> : null}
            <p className="scan-hint">
              {scanner.running
                ? 'Hold the label 10–15 cm from the camera'
                : scanner.starting
                  ? 'Starting the camera…'
                  : scanner.unavailable
                    ? 'Camera unavailable'
                    : 'Camera off'}
            </p>
          </div>
          {scanner.running ? (
            <button onClick={scanner.stop}>Stop camera</button>
          ) : (
            <button className="primary" onClick={scanner.start} disabled={!scanner.supported || scanner.starting}>
              {scanner.starting ? 'Starting…' : 'Start camera'}
            </button>
          )}
          {scanner.error ? <p className="critical">{scanner.error}</p> : null}
          {scanner.unavailable ? (
            <p className="muted" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
              {scanner.unavailable} Or open the label with the phone’s own camera — the code is a link.
            </p>
          ) : null}
        </div>

        <form
          className="panel panel--padded stack"
          style={{ gap: 10, alignSelf: 'start' }}
          onSubmit={(e) => {
            e.preventDefault()
            void resolve(typed)
          }}
        >
          <label className="field">
            <span className="field-label">Or type the serial</span>
            <input
              className="serial scan-input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="K7F38"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              maxLength={7}
            />
          </label>
          <button type="submit" className="primary" disabled={typed.trim() === ''}>
            Look up
          </button>
          {status ? <p className="muted" style={{ margin: 0 }}>{status}</p> : null}
        </form>
      </div>
    </div>
  )
}
